/**
 * StreakBadge - Compact streak indicator for nav/header
 * 
 * Shows current streak in a subtle, unobtrusive way.
 * Clicking opens the full EQ Hub.
 * 
 * Design: EQ.app Brand Guidelines
 */

import React from 'react';
import { Flame } from 'lucide-react';
import { useStreak } from '../../contexts/EngagementContext';

// Tier colors
const TIER_COLORS = {
  starting: '#9ca3af',
  building: '#f59e0b',
  strong: '#5a20ff',
  master: '#52dcb3',
  legendary: '#ec4899',
};

/**
 * StreakBadge - Shows current streak count
 * 
 * @param {function} onClick - Handler when badge is clicked (typically opens Hub)
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} showLabel - Whether to show "day streak" label
 * @param {string} className - Additional CSS classes
 */
export default function StreakBadge({ 
  onClick, 
  size = 'md', 
  showLabel = false,
  className = '' 
}) {
  const streak = useStreak();
  
  const color = TIER_COLORS[streak.tier] || TIER_COLORS.starting;
  
  // Size configurations
  const sizes = {
    sm: {
      badge: 'px-1.5 py-0.5 text-[10px]',
      icon: 'w-3 h-3',
      gap: 'gap-0.5',
    },
    md: {
      badge: 'px-2 py-1 text-xs',
      icon: 'w-3.5 h-3.5',
      gap: 'gap-1',
    },
    lg: {
      badge: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-1.5',
    },
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  
  // Don't show if no streak
  if (streak.current === 0) {
    return null;
  }
  
  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.();
  };
  
  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center ${sizeConfig.gap} ${sizeConfig.badge}
        rounded-full font-semibold transition-all
        hover:scale-105 hover:shadow-md
        ${className}
      `}
      style={{
        background: `${color}15`,
        color: color,
      }}
      title={`${streak.current} day streak - ${streak.tier}`}
    >
      <Flame 
        className={`${sizeConfig.icon} ${streak.current >= 7 ? 'animate-pulse' : ''}`}
      />
      <span>{streak.current}</span>
      {showLabel && (
        <span className="opacity-70">
          day{streak.current !== 1 ? 's' : ''}
        </span>
      )}
    </button>
  );
}

/**
 * StreakBadgeInline - Even more compact version for inline use
 */
export function StreakBadgeInline({ onClick }) {
  const streak = useStreak();
  const color = TIER_COLORS[streak.tier] || TIER_COLORS.starting;
  
  if (streak.current === 0) return null;
  
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-0.5 text-xs font-medium hover:opacity-80 transition-opacity"
      style={{ color }}
    >
      <Flame className="w-3 h-3" />
      <span>{streak.current}</span>
    </button>
  );
}

/**
 * StreakBadgeLarge - Larger version for profile/hub
 */
export function StreakBadgeLarge({ onClick, showBest = true }) {
  const streak = useStreak();
  const color = TIER_COLORS[streak.tier] || TIER_COLORS.starting;
  
  return (
    <button
      onClick={onClick}
      className="p-4 rounded-2xl border border-black/5 bg-white hover:shadow-lg transition-all text-left w-full"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: `${color}15` }}
          >
            <Flame className="w-6 h-6" style={{ color }} />
          </div>
          <div>
            <p className="text-2xl font-bold font-serif" style={{ color }}>
              {streak.current}
            </p>
            <p className="text-xs text-black/50">
              day{streak.current !== 1 ? 's' : ''} streak
            </p>
          </div>
        </div>
        
        {showBest && streak.longest > streak.current && (
          <div className="text-right">
            <p className="text-xs text-black/40">Best</p>
            <p className="text-lg font-semibold text-black/60">{streak.longest}</p>
          </div>
        )}
      </div>
    </button>
  );
}






